import { Flex, Box, Text } from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNull, isEmpty } from 'lodash-es';
import * as featureService from '../../../../../../utils/featureService';
import useDollarsTextFromCents from '../../../../../../hooks/useDollarsTextFromCents';
import membershipTierService from '../../../../../../service/membershipTierService';
import { translateModel } from '../../../../../../utils/translate';
import { SECTION_STATUS } from '../../../../constants';
import { useAppContext } from '../../../../context/appContext';
import {
  Loading as LoadingIcon,
  User as UserIcon,
} from '../../../../../context/Chakra/Icons';
import ErrorPanel from '../../components/ErrorPanel';
import MemberCodeButton from '../../components/MemberCode';
import { MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL } from '../../constants/memberTiers';
import { useProfileContext } from '../../context/profileContext';
import MemberDetailList from './sections/MemberDetailList';
import MemberExtendBalance from './sections/MemberExtendBalance';
import MemberUpgradeTier from './sections/MemberUpgradeTier';
import { getExtendBalanceStatusWordingConfig } from './utils';

const DESKTOP_VIEW_BREAKPOINT = 960;

const MemberReminder = () => {
  const { slFeatureService, userSetting } = useAppContext();
  const { userData } = useProfileContext();
  const { t, i18n } = useTranslation();
  const getDollarsTextFromCents = useDollarsTextFromCents();

  const [merchantTiers, setMerchantTiers] = useState([]);
  const [isDesktopView, setIsDesktopView] = useState(
    () => window.innerWidth >= DESKTOP_VIEW_BREAKPOINT,
  );
  const [sectionStatus, setSectionStatus] = useState(SECTION_STATUS.INITIAL);
  const membershipTier = userData.membership_tier;
  const membershipTierGap = userData.membership_tier_gap;
  const isUpgradeReminderActive =
    userSetting?.upgrade_reminder?.status === 'active';
  const showDetail =
    merchantTiers.length > 0 &&
    (isUpgradeReminderActive || userData.membership_tier_id);
  const isTopLevel =
    isNull(membershipTierGap?.next_tier) && !isEmpty(membershipTier);
  const isTierUnlimit =
    !userData.tier_expires_at ||
    membershipTier.valid_period.type === 'unlimited';
  const hasExtendReminder = slFeatureService.hasFeature('extend_reminder');
  const hasSlPosAdmin = featureService.hasFeature('sl_pos_admin');
  const hasSlPosCustomerOnlineRegister = featureService.hasFeature(
    'sl_pos_customer_online_register',
  );

  const upgradeRule = useMemo(
    () =>
      membershipTierGap?.next_tier?.membership_tier_rules?.find(
        (each) => each.effect_type === 'upgrade',
      ),
    [membershipTierGap?.next_tier?.membership_tier_rules],
  );

  const extendRule = useMemo(
    () =>
      membershipTier?.membership_tier_rules?.find(
        (each) => each.effect_type === 'extend',
      ),
    [membershipTier?.membership_tier_rules],
  );

  const extendBalanceStatusWording = useMemo(() => {
    if (
      !hasExtendReminder ||
      !isUpgradeReminderActive ||
      isEmpty(membershipTier) ||
      isTierUnlimit
    ) {
      return '';
    }

    const { user_spendings_for_extend, extend_total_spending } =
      membershipTierGap;
    const userSpendingForExtend = user_spendings_for_extend ?? 0;
    const extendTotalSpending = extend_total_spending ?? 0;
    const extendGap = extendTotalSpending - userSpendingForExtend;
    const isWithInterval =
      extendRule?.condition_interval.type ===
      MEMBER_TIER_EFFECT_TYPE_WITHIN_INTERVAL;

    const currentExtendDollar = () => {
      if (isWithInterval) {
        return getDollarsTextFromCents(
          extendGap <= 0 ? extendTotalSpending : extendGap,
        );
      }

      return getDollarsTextFromCents(extendTotalSpending);
    };
    const extendBalanceStatusWordingConfig =
      getExtendBalanceStatusWordingConfig(extendRule, t, {
        spending: getDollarsTextFromCents(userSpendingForExtend),
        dollar: currentExtendDollar(),
      });
    const topLevelStatus = isTopLevel ? 'TopLevel' : '';
    const conditionType = isWithInterval ? 'withinInterval' : 'singlePurchase';
    const canExtend = extendGap <= 0 ? 'CanExtend' : '';
    return extendBalanceStatusWordingConfig[
      `${conditionType}${topLevelStatus}${canExtend}`
    ];
  }, [
    extendRule,
    getDollarsTextFromCents,
    hasExtendReminder,
    isTopLevel,
    isUpgradeReminderActive,
    membershipTier,
    membershipTierGap,
    t,
    isTierUnlimit,
  ]);

  const handleReload = useCallback(
    () => setSectionStatus(SECTION_STATUS.INITIAL),
    [],
  );

  useEffect(() => {
    (async () => {
      if (sectionStatus === SECTION_STATUS.INITIAL) {
        try {
          setSectionStatus(SECTION_STATUS.LOADING);
          const { data } = await membershipTierService.getTiers();
          setMerchantTiers(data);
          setSectionStatus(SECTION_STATUS.SUCCESS);
        } catch (e) {
          console.error(e);
          setSectionStatus(SECTION_STATUS.FAILURE);
        }
      }
    })();
  }, [sectionStatus]);

  useEffect(() => {
    const handleWindowResize = () => {
      const nextResult = window.innerWidth >= DESKTOP_VIEW_BREAKPOINT;
      if (nextResult !== isDesktopView) {
        setIsDesktopView(nextResult);
      }
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isDesktopView]);

  return slFeatureService.hasFeature('membership_tiers') ? (
    <>
      {sectionStatus === SECTION_STATUS.LOADING && (
        <Flex alignItems="center" justifyContent="center">
          <LoadingIcon />
        </Flex>
      )}
      {sectionStatus === SECTION_STATUS.SUCCESS && (
        <div>
          <Flex
            paddingBottom="20px"
            alignItems="center"
            flexWrap="wrap"
            columnGap="10px"
            rowGap="8px"
            className="member-center-profile-section_title"
            data-e2e-id="member-center-profile-title_container"
          >
            <Flex flex="1 1 auto" minWidth="0" alignItems="center">
              <Box marginRight="10px" fontSize="0px">
                <UserIcon
                  fontSize="20px"
                  color="#dddddd"
                  className="icon-user member-center-profile__section-title-icon"
                />
              </Box>
              <Text
                variant="sectionTitle"
                className="member-center-profile__section-title-text"
              >
                {userData.name}
              </Text>
              <Text variant="tierTag" marginLeft="10px">
                {membershipTier?.name_translations
                  ? translateModel(
                      membershipTier?.name_translations,
                      i18n.language,
                    )
                  : t('users.member.normal-level')}
              </Text>
            </Flex>
            {hasSlPosAdmin &&
              hasSlPosCustomerOnlineRegister &&
              !isDesktopView &&
              userData._id && (
                <Box flexShrink={0}>
                  <MemberCodeButton memberCode={userData._id} />
                </Box>
              )}
          </Flex>
          <Flex flexWrap="wrap" margin="0 -10px">
            {showDetail && (
              <Box
                padding="0 10px 20px 10px"
                flexBasis={isDesktopView ? '50%' : '100%'}
                data-e2e-id="member-center-profile-detail_container"
              >
                <MemberDetailList
                  merchantTiers={merchantTiers}
                  extendBalanceStatusWording={extendBalanceStatusWording}
                  extendRule={extendRule}
                  upgradeRule={upgradeRule}
                />
              </Box>
            )}
            {isUpgradeReminderActive &&
              merchantTiers.length > 0 &&
              membershipTierGap.next_tier && (
                <Box
                  padding="0 10px 20px 10px"
                  flexBasis={isDesktopView ? '50%' : '100%'}
                  data-e2e-id="member-center-profile-upgrade-tier_container"
                >
                  <MemberUpgradeTier upgradeRule={upgradeRule} />
                </Box>
              )}
            {isUpgradeReminderActive && isTopLevel && !isTierUnlimit && (
              <Box
                padding="0 10px 20px 10px"
                flexBasis={isDesktopView ? '50%' : '100%'}
                data-e2e-id="member-center-profile-top-level_container"
              >
                <MemberExtendBalance
                  extendBalanceStatusWording={extendBalanceStatusWording}
                  extendRule={extendRule}
                  upgradeRule={upgradeRule}
                />
              </Box>
            )}
          </Flex>
        </div>
      )}
      {sectionStatus === SECTION_STATUS.FAILURE && (
        <ErrorPanel hideErrorImg onReload={handleReload} />
      )}
    </>
  ) : null;
};

MemberReminder.propsType = {};

export default MemberReminder;
